import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AiSearchIcon,
  Box,
  DropdownInput,
  LinkButton,
  Option,
  OptionKey,
  RegularField,
  Text,
  useDebounce,
  useResponsive,
} from '@common-fe/common-fe';

import { AccountOrder } from '@/modules/UseMyMoney/queries';
import { useDocumentOcrStore, useMyMoneyFormStore } from '@/modules/UseMyMoney/stores';
import { GroupServicesKey } from '@/modules/UseMyMoney/UseMyMoney.types';
import { useServiceCategoryRenderWrapper } from '@/modules/UseMyMoney/widgets/FieldSetup/hooks';

import AiSuggestedServicesList from '../AiSuggestedServicesList/AiSuggestedServicesList';

import ServiceDescriptionFieldHeader from './ServiceDescriptionFieldHeader';

interface Props {
  serviceOptions?: Option[];
  aiSuggestedAccountOrdersArrays?: AccountOrder[][];
  isAiSuggestedAccountOrdersLoading?: boolean;
  handleChangeService: (serviceId?: string | OptionKey) => void;
  servicesGrouping: GroupServicesKey;
  setServicesGrouping: (groupingType: GroupServicesKey) => void;
  isDisabled?: boolean;
}

const ServiceDescriptionField: React.FC<Props> = ({
  serviceOptions,
  aiSuggestedAccountOrdersArrays,
  isAiSuggestedAccountOrdersLoading,
  handleChangeService,
  servicesGrouping,
  setServicesGrouping,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { values, handleSetFormValues } = useMyMoneyFormStore();
  const { ocrDocument } = useDocumentOcrStore();

  const { serviceCategoryRenderWrapper } = useServiceCategoryRenderWrapper({
    servicesGrouping,
    accountOrdersArraysWithUniqServices: ocrDocument?.accountOrdersArraysWithUniqServices,
    isMobile,
  });
  const [searchString, setSearchString] = useState(values?.aiServicesSearchString || '');
  const debounceSearchString = useDebounce(searchString, 1000);

  useEffect(() => {
    handleSetFormValues({ aiServicesSearchString: debounceSearchString });
  }, [handleSetFormValues, debounceSearchString]);

  return (
    <Box data-testid="service-field" width="100%">
      <RegularField
        name="ai-service-search"
        testId="ai-service-search"
        value={searchString}
        onChange={(value) => setSearchString(String(value))}
        placeholder={isMobile
          ? t('Describe the expense you had')
          : t('Describe the expense you had (e.g., braces)')}
        rightIcon={<AiSearchIcon />}
        editMode
        disabled={isDisabled}
      />

      <Box margin={{ top: 'spacing12' }}>
        <DropdownInput
          testId="service"
          value={values?.customServiceId}
          onChangeValue={handleChangeService}
          options={serviceOptions}
          placeholder={t('Search')}
          groupTitleRenderWrapper={serviceCategoryRenderWrapper}
          dropdownProps={{ height: { min: 'auto', max: isMobile ? '320px' : '360px' }}}
          defaultAutoComplete="off"
          disabled={isDisabled}
          dropdownHeader={
            <ServiceDescriptionFieldHeader
              servicesGrouping={servicesGrouping}
              setServicesGrouping={setServicesGrouping}
            />
          }
          clearSearchOnFocus
        >
          <Text>
            Or,{' '}
            <LinkButton
              testId="browse-all-services"
              linkInlineMode
            >
              {t('Browse All Services')}
            </LinkButton>
          </Text>
        </DropdownInput>
      </Box>

      <AiSuggestedServicesList
        searchString={debounceSearchString}
        aiSuggestedAccountOrdersArrays={aiSuggestedAccountOrdersArrays}
        isAiSuggestedAccountOrdersLoading={isAiSuggestedAccountOrdersLoading}
        handleChangeService={handleChangeService}
      />
    </Box>
  );
};

export default ServiceDescriptionField;
